import { useAuth } from '@hourly/contexts/AuthContext';
import { useRequest } from '@hourly/hooks/useRequest';
import type { AxiosError } from 'axios';
import { useState } from 'react';
import { switchUserRequest } from '~/routes/__auth/_shared/data/requests/auth.request';
import {
  type LoginCredentials,
  sessionRequest,
  validateMagicTokenRequest,
  verifySessionCodeRequest,
} from '../requests/auth.request';

export const LoginStep = {
  SignIn: 'SignIn',
  MagicLinkSent: 'MagicLinkSent',
  CantLogin: 'CantLogin',
  CodeVerification: 'CodeVerification',
} as const;

export type LoginStep = (typeof LoginStep)[keyof typeof LoginStep];

export const useLogin = (magicLinkToken: string | null, isMobile: boolean) => {
  const { updateSessionState } = useAuth();

  const [step, setStep] = useState<LoginStep>(LoginStep.SignIn);
  const [verificationCodeError, setVerificationCodeError] = useState('');

  const { status: loginUserStatus, request: loginUser } = useRequest(
    async (credentials: LoginCredentials) => {
      await sessionRequest(credentials);
      setStep(LoginStep.MagicLinkSent);
    }
  );

  const { status: validateMagicTokenStatus, request: validateMagicToken } = useRequest(
    async (token: string | null) => {
      if (token) {
        await validateMagicTokenRequest(token);
        setStep(LoginStep.CodeVerification);
      }
    },
    {
      requestOnMount: !!magicLinkToken && !isMobile,
      initialParams: magicLinkToken,
      onError: (error, { triggerGlobalNotifyError }) => {
        const PARTNER_WITHOUT_PHONE_NUMBER = 303;

        const errorReceived = error as AxiosError<{ error_code: number }>;
        const errorCode = errorReceived.response?.data.error_code;

        if (errorCode === PARTNER_WITHOUT_PHONE_NUMBER) {
          setStep(LoginStep.CantLogin);
          return;
        }

        triggerGlobalNotifyError();
      },
    }
  );

  const { status: verifyCodeStatus, request: verifyCode } = useRequest(
    async ({ code, magic_token }: { code: string; magic_token: string }) => {
      const { partners } = await verifySessionCodeRequest(code, magic_token);

      if (!partners?.length) {
        setStep(LoginStep.SignIn);
        throw "User doesn't have partners to log in";
      }

      /**
       * The verify endpoint just returns a single partner.
       */
      const { user, partner } = await switchUserRequest(partners[0].id);

      updateSessionState({
        userId: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        profilePhoto: user.profilePhoto,
        partnerId: partner.id,
        isInOnboarding: partner.isInOnboarding,
      });

      return partner;
    },
    {
      onError: () => {
        setVerificationCodeError('Invalid Code');
      },
    }
  );

  return {
    step,
    isLoading:
      loginUserStatus.isLoading || verifyCodeStatus.isLoading || validateMagicTokenStatus.isLoading,
    isValidatingMagicToken: validateMagicTokenStatus.isLoading,
    verificationCodeError,
    loginUser,
    verifyCode,
    validateMagicToken,
  };
};
